import { useEffect, useState } from 'react';

import { Select, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { patientsApiSlice } from '@/store/slices/api/patientsApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { useAppDispatch } from '@/store/hooks';
import { INACTIVE_STATUS_REASONS } from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/DetailsSection/PatientStatusModal/constants';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import CustomModal from '@/ui/Modal';

interface ChangeInactiveReasonModalProps {
  opened: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  currentReason?: string | null;
  currentExplanation?: string | null;
}

const REASON_LABELS: Record<string, string> = {
  'Eligibility - Incarcarated': 'Eligibility - Incarcerated',
};

const REASON_OPTIONS = INACTIVE_STATUS_REASONS.map((reasonOption) => ({
  value: reasonOption,
  label: REASON_LABELS[reasonOption] ?? reasonOption,
}));

export function ChangeInactiveReasonModal({
  opened,
  onClose,
  patientId,
  patientName,
  currentReason,
  currentExplanation,
}: ChangeInactiveReasonModalProps) {
  const [reason, setReason] = useState<string | null>(null);
  const [explanation, setExplanation] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [updatePatient] = useUpdatePatientMutation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!opened) return;
    setReason(currentReason?.trim() || null);
    setExplanation(currentExplanation?.trim() || '');
  }, [opened, currentReason, currentExplanation]);

  const isOther = reason === 'Other';
  const explanationText = explanation.trim();
  const reasonOptions =
    reason && !REASON_OPTIONS.some((option) => option.value === reason)
      ? [{ value: reason, label: reason }, ...REASON_OPTIONS]
      : REASON_OPTIONS;

  const handleSubmit = async () => {
    if (!reason || (isOther && !explanationText) || isSaving) return;
    setIsSaving(true);
    try {
      await updatePatient({
        patient: {
          id: patientId,
          statusReason: reason,
          ...(isOther ? { statusOtherReason: explanationText } : {}),
        },
      }).unwrap();
      dispatch(
        patientsApiSlice.util.invalidateTags([{ type: API_SLICE.Patient, id: patientId }]),
      );
      notifications.show({
        title: 'Inactive reason updated',
        message: `${patientName} stays inactive. The inactive date is unchanged.`,
        color: 'teal',
      });
      onClose();
    } catch (error) {
      const data = (error as { data?: { message?: string; details?: { msg?: string }[] } })?.data;
      const detail = data?.details?.find((item) => item.msg)?.msg;
      notifications.show({
        title: 'Error',
        message: detail && detail !== 'Bad Request' ? detail : 'Failed to update the inactive reason.',
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CustomModal
      header={{ title: 'Change inactive reason' }}
      modalProps={{ opened, onClose, size: 'md' }}
      secondaryButton={{ text: 'Cancel', onClick: onClose }}
      primaryButton={{
        text: 'Save reason',
        onClick: handleSubmit,
        isDisabled: !reason || (isOther && !explanationText),
        isLoading: isSaving,
      }}>
      <Stack gap="md" p="md">
        <Text size="sm" c="dimmed">
          This updates the inactive reason for {patientName} and leaves the patient inactive. The
          inactive date stays the same.
        </Text>
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Inactive reason <Text span c="red">*</Text>
          </Text>
          <Select
            placeholder="Select a reason..."
            data={reasonOptions}
            value={reason}
            onChange={setReason}
            searchable
            allowDeselect={false}
          />
        </Stack>
        {isOther && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Inactive explanation <Text span c="red">*</Text>
            </Text>
            <Textarea
              placeholder="Required when the reason is Other"
              minRows={3}
              autosize
              value={explanation}
              onChange={(e) => setExplanation(e.currentTarget.value)}
            />
          </Stack>
        )}
      </Stack>
    </CustomModal>
  );
}
